import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  MapPin,
  Mail,
  Link as LinkIcon,
  Phone,
  Edit,
  Globe,
  Users,
  Lock,
  Eye,
  Check,
} from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { ProfileBadges } from '@/components/profile/profile-badges';
import { AvatarUpload } from './avatar-upload';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

function formatPhoneNumber(phoneNumber: string): string {
  if (!phoneNumber) return '';

  const cleaned = phoneNumber.replace(/\D/g, '');

  // US numbers with country code (1)
  if (cleaned.startsWith('1') && cleaned.length === 11) {
    return `+1 (${cleaned.substring(1, 4)}) ${cleaned.substring(4, 7)}-${cleaned.substring(7)}`;
  }
  // US numbers without country code
  else if (/^\d{10}$/.test(cleaned)) {
    return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
  }
  // International numbers with country code (2+ digits)
  else if (/^\d{11,15}$/.test(cleaned)) {
    const countryCode = cleaned.substring(0, cleaned.length - 10);
    const nationalNumber = cleaned.substring(cleaned.length - 10);
    return `+${countryCode} (${nationalNumber.substring(0, 3)}) ${nationalNumber.substring(3, 6)}-${nationalNumber.substring(6)}`;
  }
  // Numbers that already include formatting
  else if (phoneNumber.includes('(') || phoneNumber.includes('+')) {
    return phoneNumber;
  }
  // Fallback for any other format
  else {
    return phoneNumber;
  }
}

interface ProfileHeaderProps {
  isEditing: boolean;
  profileData?: any;
  isPublicView?: boolean;
}

export function ProfileHeader({
  isEditing,
  profileData,
  isPublicView = false,
}: ProfileHeaderProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState(profileData || {});
  const [editMode, setEditMode] = useState(false);
  const [editedProfile, setEditedProfile] = useState({ ...profileData } || {});

  const handleSave = async () => {
    if (!user || !profile?.id) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: editedProfile.first_name,
          last_name: editedProfile.last_name,
          title: editedProfile.title,
          summary: editedProfile.summary,
          email: editedProfile.email,
          phone: editedProfile.phone,
          location: editedProfile.location,
          website: editedProfile.website,
          github: editedProfile.github,
          privacy_level: editedProfile.privacy_level,
          custom_url: editedProfile.custom_url,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Your profile has been updated',
      });

      setProfile((prev) => ({ ...prev, ...editedProfile }));
      setEditMode(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred',
      });
    }
  };

  const getUserInitials = () => {
    if (profile.first_name && profile.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
    }
    return user?.email ? user.email[0].toUpperCase() : 'U';
  };

  const getVisibilityInfo = () => {
    switch (profile.privacy_level) {
      case 'public':
        return {
          icon: Globe,
          text: 'Public',
          description: 'Visible to everyone',
        };
      case 'connections':
        return {
          icon: Users,
          text: 'Connections',
          description: 'Visible to connections only',
        };
      case 'private':
        return {
          icon: Lock,
          text: 'Private',
          description: 'Visible only to you',
        };
      default:
        return {
          icon: Globe,
          text: 'Public',
          description: 'Visible to everyone',
        };
    }
  };

  const {
    icon: VisibilityIcon,
    text: visibilityText,
    description: visibilityDescription,
  } = getVisibilityInfo();

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
        {/* Avatar */}
        <div className="relative">
          <Avatar className="h-24 w-24 border-2 border-primary/10">
            <AvatarImage
              src={profile.avatar_url}
              alt={`${profile.first_name} ${profile.last_name}`}
            />
            <AvatarFallback className="text-xl">
              {getUserInitials()}
            </AvatarFallback>
          </Avatar>
          {isEditing && (
            <AvatarUpload
              userId={profile.id}
              onUploadComplete={(url) =>
                setProfile((prev) => ({ ...prev, avatar_url: url }))
              }
            />
          )}
        </div>

        {/* Profile Info */}
        <div className="flex-1 space-y-4 text-center md:text-left">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <h2 className="text-2xl font-bold">
                {profile.first_name} {profile.last_name}
              </h2>
              {isEditing && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Select
                        value={profile.privacy_level}
                        onValueChange={(value) =>
                          setEditedProfile((prev) => ({
                            ...prev,
                            privacy_level: value,
                          }))
                        }
                      >
                        <SelectTrigger className="w-[120px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="public">
                            <div className="flex items-center gap-2">
                              <Globe className="h-4 w-4" />
                              <span>Public</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="connections">
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4" />
                              <span>Connections</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="private">
                            <div className="flex items-center gap-2">
                              <Lock className="h-4 w-4" />
                              <span>Private</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{visibilityDescription}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>

            <div className="flex items-center gap-2">
              {isEditing && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        asChild
                        className="h-8 w-8"
                      >
                        <Link
                          to={`/p/${profile.custom_url || profile.id}`}
                          target="_blank"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>Preview Public View</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {isEditing && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => setEditMode(true)}
                        className="h-8 w-8"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>Edit Profile</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>

          {editMode ? (
            <Input
              value={editedProfile.title || ''}
              onChange={(e) =>
                setEditedProfile((prev) => ({ ...prev, title: e.target.value }))
              }
              placeholder="Professional title"
            />
          ) : (
            <p className="text-muted-foreground">
              {profile.title || 'No title provided'}
            </p>
          )}

          {/* Profile badges */}
          {profile.id && (
            <ProfileBadges profileId={profile.id} profileData={profile} />
          )}
        </div>
      </div>

      {/* Summary */}
      <div>
        {editMode ? (
          <Textarea
            value={editedProfile.summary || ''}
            onChange={(e) =>
              setEditedProfile((prev) => ({
                ...prev,
                summary: e.target.value,
              }))
            }
            placeholder="Write a short professional summary..."
            className="min-h-[100px]"
          />
        ) : (
          <p className="text-sm text-muted-foreground whitespace-pre-line">
            {profile.summary || 'No summary provided'}
          </p>
        )}
      </div>

      {/* Contact Information */}
      {(!isPublicView || editMode) && (
        <div className="flex flex-col md:flex-row gap-4 md:gap-8">
          <div className="flex-1 space-y-2">
            <h3 className="text-sm font-semibold">Contact Information</h3>

            <div className="space-y-3">
              {(profile.location || editMode) && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  {editMode ? (
                    <Input
                      value={editedProfile.location || ''}
                      onChange={(e) =>
                        setEditedProfile((prev) => ({
                          ...prev,
                          location: e.target.value,
                        }))
                      }
                      placeholder="Location"
                      className="h-8"
                    />
                  ) : (
                    <span className="text-sm">{profile.location}</span>
                  )}
                </div>
              )}

              {(profile.email || editMode) && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  {editMode ? (
                    <Input
                      value={editedProfile.email || ''}
                      onChange={(e) =>
                        setEditedProfile((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      placeholder="Email"
                      className="h-8"
                    />
                  ) : (
                    <a
                      href={`mailto:${profile.email}`}
                      className="text-sm hover:text-primary transition-colors"
                    >
                      {profile.email}
                    </a>
                  )}
                </div>
              )}

              {(profile.phone || editMode) && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  {editMode ? (
                    <Input
                      value={editedProfile.phone || ''}
                      onChange={(e) =>
                        setEditedProfile((prev) => ({
                          ...prev,
                          phone: e.target.value,
                        }))
                      }
                      placeholder="Phone number"
                      className="h-8"
                    />
                  ) : (
                    <a
                      href={`tel:${profile.phone}`}
                      className="text-sm hover:text-primary transition-colors"
                    >
                      {formatPhoneNumber(profile.phone)}
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 space-y-2">
            <h3 className="text-sm font-semibold">Online Presence</h3>

            <div className="space-y-3">
              {(profile.website || editMode) && (
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-4 w-4 text-muted-foreground" />
                  {editMode ? (
                    <Input
                      value={editedProfile.website || ''}
                      onChange={(e) =>
                        setEditedProfile((prev) => ({
                          ...prev,
                          website: e.target.value,
                        }))
                      }
                      placeholder="Website URL"
                      className="h-8"
                    />
                  ) : (
                    <a
                      href={profile.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm hover:text-primary transition-colors"
                    >
                      {profile.website &&
                        profile.website.replace(/^https?:\/\//, '')}
                    </a>
                  )}
                </div>
              )}

              {(profile.github || editMode) && (
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  {editMode ? (
                    <Input
                      value={editedProfile.github || ''}
                      onChange={(e) =>
                        setEditedProfile((prev) => ({
                          ...prev,
                          github: e.target.value,
                        }))
                      }
                      placeholder="GitHub profile URL"
                      className="h-8"
                    />
                  ) : (
                    <a
                      href={profile.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm hover:text-primary transition-colors"
                    >
                      {profile.github &&
                        profile.github.replace(
                          /^https?:\/\/(www\.)?github\.com\//,
                          ''
                        )}
                    </a>
                  )}
                </div>
              )}

              {/* Custom URL field */}
              {editMode && (
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-4 w-4 text-muted-foreground" />
                  <Input
                    value={editedProfile.custom_url || ''}
                    onChange={(e) =>
                      setEditedProfile((prev) => ({
                        ...prev,
                        custom_url: e.target.value,
                      }))
                    }
                    placeholder="Custom profile URL"
                    className="h-8"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Save/Cancel buttons when in edit mode */}
      {editMode && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setEditMode(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Check className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
}
